import type { Status } from '@components/utils/status';
import type { RcIconType } from '@components/RcIcon/types';

export const SECTION_TYPE = {
  PRIMARY:   'primary',
  SECONDARY: 'secondary',
} as const;

export type SectionType = typeof SECTION_TYPE[keyof typeof SECTION_TYPE];

export type SectionMode = 'with-header' | 'no-header';

export const SECTION_BACKGROUND = {
  PRIMARY:   'primary',
  SECONDARY: 'secondary',
} as const;

export type SectionBackground = typeof SECTION_BACKGROUND[keyof typeof SECTION_BACKGROUND];

// ---------------------------------------------------------------------------
// Badge helpers
// ---------------------------------------------------------------------------

export interface BadgeConfig {
  /** Display text inside the badge. */
  label: string;
  /** Status colour of the badge. */
  status: Status;
  /** Optional tooltip text shown on hover. */
  tooltip?: string;
}

export interface RcSectionBadgesProps {
  badges: BadgeConfig[];
}

// ---------------------------------------------------------------------------
// Action helpers
// ---------------------------------------------------------------------------

export interface ActionConfig {
  /** Button label. When omitted the button renders as icon-only (ghost). */
  label?: string;
  /** Icon shown on the button (left position for labeled, sole content for icon-only). */
  icon?: RcIconType;
  /** Accessible label for the button. Required when `label` is omitted so screen readers can describe the action. */
  ariaLabel?: string;
  /** Callback invoked when the action is clicked. */
  action: () => void;
}

export interface RcSectionActionsProps {
  actions: ActionConfig[];
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

export interface RcSectionProps {
  /**
   * Visual type of the section.
   * - Primary: no lateral padding, border-radius, or background (inherits from parent). Bolder title (700).
   * - Secondary: has lateral padding (16px), border-radius (8px), and background color. Lighter title (600).
   */
  type: SectionType;

  /**
   * Controls whether the section renders with a header or without.
   * - 'with-header': renders the full header (title, badges, actions, errors).
   * - 'no-header': hides the header entirely; content padding adjusts to 16px vertical.
   */
  mode: SectionMode;

  /**
   * Background color of the section. When omitted, automatically alternates
   * from the parent RcSection's background (primary -> secondary -> primary).
   */
  background?: SectionBackground;

  /**
   * Whether the section can be expanded/collapsed via the header.
   */
  expandable: boolean;

  /**
   * Whether the section content is expanded. Only applies when `expandable` is true.
   * Supports `v-model:expanded`.
   */
  expanded?: boolean;

  /**
   * Whether collapsed content is unmounted instead of hidden. Defaults to false,
   * which keeps collapsed content mounted so form fields inside it stay
   * registered with their validation. Only applies when `expandable` is true.
   */
  unmountOnHide?: boolean;

  /**
   * The section title text. Can also be provided via the `title` slot.
   */
  title?: string;
}
